import { PrivilegeChecker } from 'omni-common-ui';
import Privileges from 'domain/Privileges';
import TabSwitcher, { TabCodes } from 'domain/TabSwitcher';
import Homework from './component';
import { routes as mentor } from './Mentor';
import { routes as studentApp } from './StudentApp';
import { routes as practicePlay } from './PracticePlay';
import { routes as onlineUsage } from './OnlineUsage';
import { routes as unitQuiz } from './UnitQuiz';
import { routes as reader } from './Reader';
import { routes as vocabulary } from './Vocabulary';
import paperBased from './PaperBased';
import { accessors as sessionPlanAccessors } from 'data/SessionPlans';
import ViewNav from 'components/ViewNav';
import { actions as groupActions } from 'containers/Group';

export const TRAILBLAZERS_APP = 'trailblazers-app';
const MENTOR = 'mentor';
const PAPER_BASE = 'paper-based';
export const ONLINE = 'online';
export const PRACTICE_PLAY = 'practice-play';
export const ONLINE_USAGE = 'online-usage';
export const SMALL_STARS_APP = 'small-stars-app';
export const UNIT_QUIZ = 'unit-quiz';
export const READING = 'reading';
export const VOCABULARY = 'vocabulary';

const TAB_MODULE_MAPPING = {
  [TRAILBLAZERS_APP]: TabCodes.TrailblazersApp,
  [MENTOR]: TabCodes.Mentor,
  [PAPER_BASE]: TabCodes.PaperBased,
  [ONLINE]: TabCodes.Online,
  [PRACTICE_PLAY]: TabCodes.PracticePlay,
  [ONLINE_USAGE]: TabCodes.OnlineUsage,
  [SMALL_STARS_APP]: TabCodes.SmallStarsApp,
  [UNIT_QUIZ]: TabCodes.UnitQuiz,
  [READING]: TabCodes.Reader,
  [VOCABULARY]: TabCodes.Vocabulary,
};

export default ({ getState }) => ({
  appBars: {
    sticky: {
      'view-nav': {
        component: ViewNav,
        order: 2,
        props: {
          hideExpandable: true,
        },
      },
    },
  },
  path: 'homework',
  component: Homework,
  canAccess: ({ params }) =>
    PrivilegeChecker.hasPrivilege(getState(), Privileges.Homework.View) &&
    TabSwitcher.isTabEnabled(getState(), params.groupId, TabCodes.MarkHomework),
  indexRoute: {
    isVisible: () =>
      PrivilegeChecker.hasPrivilege(getState(), Privileges.Homework.View),
  },
  breadcrumbLabels: {
    label: 'Practice',
    clickable: false,
  },
  tab: {
    label: 'Digital Practice Results',
    order: 3,
    isVisible: (params) =>
      PrivilegeChecker.hasPrivilege(getState(), Privileges.Homework.View) &&
      TabSwitcher.isTabEnabled(getState(), params.groupId, TabCodes.MarkHomework),
  },
  secondLevelTabs: (props) => {
    const { groupId } = props.params;
    const currentCourseTypeTabs = getTabsByCourseType(getState(), groupId);
    return getSecondLevelTabs(getState(), groupId, props, currentCourseTypeTabs);
  },
  childRoutes: [
    studentApp,
    onlineUsage,
    mentor,
    paperBased,
    practicePlay,
    unitQuiz,
    reader,
    vocabulary,
  ],
});

function getTabsByCourseType(state, groupId) {
  const courseTypeTabs = [];
  if (groupActions.isTBCourseTypeGroup(state, groupId)) {
    if (groupActions.hasContentPath(state, groupId)) {
      courseTypeTabs.push(TRAILBLAZERS_APP, READING, VOCABULARY, MENTOR);
    } else {
      courseTypeTabs.push(TRAILBLAZERS_APP, MENTOR);
    }
  }
  if (groupActions.isHFCourseTypeGroup(state, groupId)) {
    if (groupActions.hasContentPath(state, groupId)) {
      courseTypeTabs.push(ONLINE, READING, VOCABULARY, PAPER_BASE);
    } else {
      courseTypeTabs.push(ONLINE, PAPER_BASE);
    }
  }
  if (groupActions.isSSCourseTypeGroup(state, groupId)) {
    if (groupActions.hasContentPath(state, groupId)) {
      courseTypeTabs
        .push(PRACTICE_PLAY, SMALL_STARS_APP, READING, VOCABULARY, PAPER_BASE, UNIT_QUIZ);
    } else {
      courseTypeTabs.push(PRACTICE_PLAY, SMALL_STARS_APP, PAPER_BASE, UNIT_QUIZ);
    }
  }
  if (groupActions.isFRCourseTypeGroup(state, groupId)) {
    if (groupActions.hasContentPath(state, groupId)) {
      courseTypeTabs.push(ONLINE, VOCABULARY);
    } else {
      courseTypeTabs.push(ONLINE_USAGE);
    }
  }
  return courseTypeTabs;
}

function filterEnabledTabs(state, groupId, tabs) {
  return tabs.filter((tab) => TabSwitcher.isTabEnabled(state, groupId, TAB_MODULE_MAPPING[tab]));
}

export function getCourseTypeFirstTab(state, groupId) {
  const currentCourseTypeTabs = getTabsByCourseType(state, groupId);
  return filterEnabledTabs(state, groupId, currentCourseTypeTabs)[0];
}

function getSecondLevelTabs(state, groupId, props, tabs) {
  const enabledTabs = filterEnabledTabs(state, groupId, tabs);
  const secondLevelTabs = enabledTabs.map((tab) => getSecondLevelTab(state, props, tab));
  if (secondLevelTabs.length < 2) return;
  return secondLevelTabs;
}

function getSecondLevelTab(state, { location: { pathname }, buildRoute, push, params }, type) {
  switch (type) {
    case TRAILBLAZERS_APP:
    case ONLINE:
    case MENTOR:
    case PRACTICE_PLAY:
    case SMALL_STARS_APP:
    case ONLINE_USAGE:
    case UNIT_QUIZ:
    case READING:
    case VOCABULARY:
      return {
        text: getHomeworkTabName(type),
        onClick: () => push(buildRoute(`/group/:groupId/homework/${type}`, params)),
        isActive: pathname.indexOf(type) > 0,
      };
    case PAPER_BASE:
      return {
        text: type,
        onClick: () => {
          const { groupId } = params;
          const defaultSessionId = getPreviousSessionId(state, groupId);
          return push(buildRoute(`/group/:groupId/homework/session/:sessionId/unit/:unitId/${type}`,
              Object.assign({}, params,
                {
                  sessionId: defaultSessionId,
                  unitId: sessionPlanAccessors.getFirstUnitOfSession(state, defaultSessionId),
                }
              )
          ));
        },
        isActive: pathname.indexOf(type) > 0,
      };
    default:
      break;
  }
}

function getPreviousSessionId(state, groupId) {
  return sessionPlanAccessors.getPreviousSession(state, groupId).id;
}

function getHomeworkTabName(type) {
  switch (type) {
    case PRACTICE_PLAY:
      return 'practice & play';
    case SMALL_STARS_APP:
      return 'ef small stars app';
    case TRAILBLAZERS_APP:
      return 'ef trailblazers app';
    case ONLINE_USAGE:
      return 'online usage';
    case UNIT_QUIZ:
      return 'small stars app quiz';
    default:
      return type;
  }
}



// WEBPACK FOOTER //
// ./containers/Group/Homework/routes.js